// apps/app/src/routes/Setups/List/Card/Images.tsx
import { Badge } from '@/components/ui/badge'
import * as Card from '@/components/ui/card'
import { IconButton } from '@/components/ui/icon-button'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/text'
import { api } from '@api'
import { SetupImageCarousel } from '@components/SetupImageCarousel'
import type { Setup } from '@hvf/contracts'
import { createQuery } from '@tanstack/solid-query' // Added Tanstack Query
import createEmblaCarousel from 'embla-carousel-solid'
import { ImageOff, Play } from 'lucide-solid'
import { For, Show, createEffect, createMemo, createSignal, onCleanup } from 'solid-js'
import { css, cx } from 'styled-system/css'
import { Box, Flex } from 'styled-system/jsx'
import { aspectRatio } from 'styled-system/patterns'

export const Images = (props: { setup: Setup | null | undefined }) => {
  const [emblaRef, emblaApi] = createEmblaCarousel()
  const [isHovered, setIsHovered] = createSignal(false)
  const [openImage, setOpenImage] = createSignal<number>()
  const [currentIndex, setCurrentIndex] = createSignal(0)

  const rawImages = createMemo(() => [
    { title: 'Pattern', src: props.setup?.image_pattern },
    { title: 'Macro', src: props.setup?.image_macro },
    { title: 'Trend', src: props.setup?.image_trend },
    { title: 'Trigger', src: props.setup?.image_trigger },
    { title: 'Time Stop', src: props.setup?.image_time_stop },
    { title: 'Volume by price', src: props.setup?.image_volume_by_price },
  ].filter((image): image is { title: string; src: string } => typeof image.src === 'string' && image.src.length > 0))

  const imagesQuery = createQuery(() => ({
    queryKey: ['setup-images', props.setup?.id],
    queryFn: async () => {
      const images = rawImages()

      // 2. You no longer need the `!` non-null assertions
      const supabasePaths = images
        .map(img => img.src)
        .filter(src => !src.startsWith('http'))

      const signedUrlsMap: Record<string, string> = {}

      if (supabasePaths.length > 0) {
        const { data, error } = await api().storage
          .from('setup-images')
          .createSignedUrls(supabasePaths, 3600)

        if (!error && data) {
          data.forEach((item) => {
            if (item.signedUrl && item.path) signedUrlsMap[item.path] = item.signedUrl
          })
        } else {
          console.error('Failed to fetch batch signed URLs:', error)
        }
      }

      // 3. TS now safely allows img.src as an index type
      return images.map(img => {
        if (img.src.startsWith('http')) return { title: img.title, src: img.src }
        return { title: img.title, src: signedUrlsMap[img.src] || '' }
      }).filter(img => img.src !== '')
    },
    enabled: !!props.setup?.id && rawImages().length > 0,
    staleTime: 1000 * 60 * 50,
    gcTime: 1000 * 60 * 60,
  }))

  createEffect(() => {
    const api = emblaApi()
    if (!api) return

    const onSelect = () => setCurrentIndex(api.selectedScrollSnap())

    api.on('select', onSelect)
    api.on('reInit', onSelect)
    onSelect()

    onCleanup(() => {
      api.off('select', onSelect)
      api.off('reInit', onSelect)
    })
  })

  // Safely fallback to an empty array while loading
  const resolvedImages = () => imagesQuery.data || []

  return (
    <Show when={resolvedImages().length} fallback={<Box
      border='solid 1px'
      borderColor='gray.a8'
      borderRadius='lg'
      class={cx(
        aspectRatio({ ratio: 16 / 9 }),
        css({
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          width: '100%',
        })
      )}
    >
      <Flex gap={3} color='fg.subtle'>
        <ImageOff size={15} />
        <Text>No Images</Text>
      </Flex>
    </Box >}>
      <Box position="relative">
        <SetupImageCarousel
          images={resolvedImages()}
          openImage={openImage}
          setIsOpen={setOpenImage}
        />

      <div
        class={css({
          overflow: 'hidden',
          filter: props.setup?.stage === 'spoiled' ? 'grayscale(100%)' : 'none',
          position: 'relative',
          aspectRatio: '16 / 10',
          borderRadius: '12px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: 'var(--colors-editorial-hair)',
          bg: 'editorialSoft',
        })}
        ref={emblaRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div class={css({ display: 'flex', gap: '1', height: '100%' })}>
          {/* 3. Improvement: Handle loading state gracefully */}
          <Show when={!imagesQuery.isLoading} fallback={
            <Skeleton
              class={css({
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                width: '100%',
                height: '100%',
              })} />
          }>
            <For each={resolvedImages()}>
              {(image, index) => (
                <Box
                  flex="0 0 100%"
                  minWidth={0}
                  height="100%"
                  cursor="zoom-in"
                  position="relative"
                  onClick={() => setOpenImage(index())}
                >
                  <Card.Root
                    style={{
                      'background-image': `url(${image.src})`,
                    }}
                    class={css({
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      width: '100%',
                      height: '100%',
                      borderRadius: 0,
                      border: 'none',
                    })}
                  />
                </Box>
              )}
            </For>
          </Show>
        </div>

        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width='3px'
          zIndex={2}
          pointerEvents="none"
          class={css({
            bg: props.setup?.position === 'long'
              ? 'linear-gradient(180deg, var(--colors-editorial-long), transparent)'
              : 'linear-gradient(180deg, var(--colors-editorial-short), transparent)',
          })}
        />

        <Show when={isHovered() && resolvedImages().length > 1}>
          <>
            <Flex
              class={css({
                justifyContent: 'space-between',
                position: 'absolute',
                pointerEvents: 'none',
                top: 0,
                left: 0,
                right: 0,
                height: '100%',
                alignItems: 'center',
                px: '9px',
              })}
            >
              <IconButton
                pointerEvents="auto"
                variant="plain"
                aria-label="Previous chart view"
                w='28px'
                h='28px'
                minW='28px'
                borderRadius='full'
                bg='gray.surface.bg'
                boxShadow='0 2px 8px rgba(20,22,27,.16)'
                class={css({
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'var(--colors-editorial-hair-strong)',
                })}
                onClick={() => emblaApi()?.scrollPrev()}
              >
                <span
                  aria-hidden="true"
                  class={css({ fontSize: '14px', lineHeight: 1, color: 'editorialAccentDeep' })}
                >
                  ‹
                </span>
              </IconButton>
              <IconButton
                pointerEvents="auto"
                variant="plain"
                aria-label="Next chart view"
                w='28px'
                h='28px'
                minW='28px'
                borderRadius='full'
                bg='gray.surface.bg'
                boxShadow='0 2px 8px rgba(20,22,27,.16)'
                class={css({
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'var(--colors-editorial-hair-strong)',
                })}
                onClick={() => emblaApi()?.scrollNext()}
              >
                <span
                  aria-hidden="true"
                  class={css({ fontSize: '14px', lineHeight: 1, color: 'editorialAccentDeep' })}
                >
                  ›
                </span>
              </IconButton>
            </Flex>

            <Box
              position="absolute"
              pointerEvents="none"
              bottom='10px'
              left='50%'
              class={css({ transform: 'translateX(-50%)' })}
            >
              <Badge
                pointerEvents="auto"
                bg='gray.surface.bg'
                color='editorialInk2'
                px='12px'
                py='5px'
                h='auto'
                borderRadius='8px'
                fontSize='12.5px'
                fontWeight='normal'
                boxShadow='0 3px 10px rgba(20,22,27,.14)'
                class={css({
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'var(--colors-editorial-hair-strong)',
                })}
              >
                {resolvedImages()[currentIndex()]?.title}
              </Badge>
            </Box>
          </>
        </Show>
      </div>
      </Box>
    </Show>
  )
}
